import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { UI_DATE_FORMAT } from '../../Utils/DateTime';

const getInObject = (obj, path) => {
  if (!obj || typeof obj !== 'object' || !path) {
    return undefined;
  }
  return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

const CustomDatePicker = ({
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  minDate,
  maxDate,
  error = false,
  helperText = '',
  // Shown and typed as MM/DD/YYYY; the value handed back stays YYYY-MM-DD so stored data is unchanged.
  format = UI_DATE_FORMAT,
  name,
  formik,
  className,
  ...props
}) => {
  const rawValue = formik
    ? (name ? getInObject(formik.values, name) : undefined)
    : value;

  const dateValue = useMemo(() => {
    if (!rawValue) return null;
    const parsed = dayjs(rawValue);
    return parsed.isValid() ? parsed : null;
  }, [rawValue]);

  const handleDateChange = (newValue) => {
    let formattedValue = '';
    if (newValue && dayjs.isDayjs(newValue) && newValue.isValid()) {
      formattedValue = newValue.format('YYYY-MM-DD');
    } else if (newValue && typeof newValue === 'string' && dayjs(newValue).isValid()) {
      formattedValue = dayjs(newValue).format('YYYY-MM-DD');
    }

    if (formik && name) {
      const current = getInObject(formik.values, name) ?? '';
      if (current !== formattedValue) {
        formik.setFieldValue(name, formattedValue);
      }
      return;
    }

    if (onChange) {
      const current = value ?? '';
      if (current !== formattedValue) {
        onChange(formattedValue);
      }
    }
  };

  const touchedValue = formik && name ? getInObject(formik.touched, name) : false;
  const errorValue = formik && name ? getInObject(formik.errors, name) : error;

  const fieldError = formik && name
    ? Boolean(touchedValue && errorValue)
    : Boolean(error);

  const fieldHelperText = formik && name
    ? (touchedValue && typeof errorValue === 'string' ? errorValue : '')
    : helperText;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        {...props}
        label={label}
        name={name}
        value={dateValue}
        onChange={handleDateChange}
        disabled={disabled}
        format={format}
        minDate={minDate}
        maxDate={maxDate}
        slotProps={{
          textField: {
            className,
            fullWidth: true,
            required,
            size: 'small',
            error: fieldError,
            helperText: fieldHelperText,
            onBlur: () => {
              if (formik && name) {
                formik.setFieldTouched(name, true);
              }
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default CustomDatePicker;